import {
  ExportDropdown,
  ExportDropdownProps,
} from "@components/common/export/export-dropdown";
import { Snapshot } from "@custom-types/base-types";
import { useDateTime } from "@hooks/use-date-time";
import { defaultProjectLaunchTargetSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import { downloadSnapshotsData, ExportFormat } from "@utils/file-data-export";
import { TableType } from "@utils/track-event/track-event-list";
import { useCallback } from "react";

interface Props extends Pick<ExportDropdownProps, "buttonType"> {
  /**
   * The snapshots to export: the selected snapshots when any row is selected,
   * otherwise the whole list shown in the table.
   */
  selectedSnapshots: Snapshot[];

  /** The name of the snapshots' project, used to build the file name */
  projectName: string;

  /** True if some entities are selected */
  areEntitiesSelected: boolean;
}

/** Dropdown to export snapshots in the file format selected by the user */
export function BulkExportSnapshots({
  selectedSnapshots,
  projectName,
  areEntitiesSelected,
  buttonType,
}: Props): JSX.Element {
  const { formatDate } = useDateTime();
  const { defaultOpenProjectTarget } = useAppSelector(
    defaultProjectLaunchTargetSelector
  );

  const exportHandler = useCallback(
    async (format: ExportFormat): Promise<number> => {
      return await downloadSnapshotsData({
        snapshots: selectedSnapshots,
        fileName: `${projectName} - Snapshots List`,
        formatDate,
        format,
        defaultOpenProjectTarget,
      });
    },
    [selectedSnapshots, projectName, formatDate, defaultOpenProjectTarget]
  );

  return (
    <ExportDropdown
      trackingProps={{
        dataType: TableType.snapshots,
        numberOfEntities: selectedSnapshots.length,
        areEntitiesSelected,
      }}
      exportHandler={exportHandler}
      toastMessage={{
        success: "Successfully exported snapshots.",
        error: "Cannot export snapshots. Please try again.",
      }}
      buttonTooltipText="Export snapshots"
      buttonType={buttonType}
    />
  );
}
